import React from 'react';
import {
  BuildingOffice2Icon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { Supplier } from '../../types/supplier';
import { Tooltip } from '../common/Tooltip';

interface SupplierListProps {
  suppliers: Supplier[];
  onStatusChange: (supplier: Supplier) => void;
  onEditClick: (supplier: Supplier) => void;
  onDeleteClick: (supplier: Supplier) => void;
}

const SupplierList: React.FC<SupplierListProps> = ({
  suppliers,
  onStatusChange,
  onEditClick,
  onDeleteClick
}) => {
  return (
    <>
      {/* Desktop táblázat nézet */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Név
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Kapcsolat
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Adatok
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  Státusz
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {suppliers.map(supplier => (
                <tr key={supplier.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 border mr-2">
                        {supplier.companyName ? (
                          <BuildingOffice2Icon className="h-5 w-5 text-primary-600" />
                        ) : (
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {supplier.name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {supplier.companyName && supplier.companyName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      {supplier.email && (
                        <a href={`mailto:${supplier.email}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                          {supplier.email}
                        </a>
                      )}
                      {supplier.phone && (
                        <a href={`tel:${supplier.phone}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                          {supplier.phone}
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col text-xs text-gray-600 dark:text-gray-400">
                      {supplier.taxNumber && (
                        <div>Adószám: {supplier.taxNumber}</div>
                      )}
                      {supplier.address && (
                        <div>
                          {[
                            supplier.address.postalCode,
                            supplier.address.city,
                            supplier.address.street,
                            supplier.address.houseNumber
                          ].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <button
                        onClick={() => onStatusChange(supplier)}
                        className={`p-1.5 rounded-full ${supplier.isActive
                          ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                      >
                        {supplier.isActive ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <XCircleIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-2">
                      <Tooltip content="Szerkesztés">
                        <button
                          onClick={() => onEditClick(supplier)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Törlés">
                        <button
                          onClick={() => onDeleteClick(supplier)}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil nézet - kártya elrendezés */}
      <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden">
        {suppliers.map(supplier => (
          <div key={supplier.id} className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-md border border-gray-200 dark:border-gray-700 w-full overflow-hidden">
            <div className="flex justify-between w-full">
              <div className="flex items-center mr-2 mb-1.5 sm:mb-2 max-w-[60%]">
                <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 border mr-1 sm:mr-2 flex-shrink-0">
                  {supplier.companyName ? (
                    <BuildingOffice2Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                  ) : (
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                  )}
                </div>
                <div className="min-w-0 w-full overflow-hidden">
                  <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {supplier.name}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 truncate">
                    {supplier.companyName && supplier.companyName}
                  </div>
                </div>
              </div>

              <div className="flex items-center flex-shrink-0 ml-auto space-x-0 sm:space-x-1">
                <button
                  onClick={() => onStatusChange(supplier)}
                  className={`p-1 rounded-full ${supplier.isActive
                    ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                    : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                >
                  {supplier.isActive ? (
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
                <button
                  onClick={() => onEditClick(supplier)}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <PencilSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => onDeleteClick(supplier)}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
              {supplier.email && (
                <a href={`mailto:${supplier.email}`} className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 max-w-full truncate">
                  <EnvelopeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{supplier.email}</span>
                </a>
              )}
              {supplier.phone && (
                <a href={`tel:${supplier.phone}`} className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 max-w-full truncate">
                  <PhoneIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{supplier.phone}</span>
                </a>
              )}
            </div>

            {supplier.taxNumber && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                Adószám: {supplier.taxNumber}
              </div>
            )}

            {supplier.address && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                {[
                  supplier.address.postalCode,
                  supplier.address.city,
                  supplier.address.street,
                  supplier.address.houseNumber
                ].filter(Boolean).join(' ')}
              </div>
            )}

            {supplier.notes && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-1.5 sm:p-2 rounded overflow-hidden">
                <div className="line-clamp-2">{supplier.notes}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default SupplierList; 